import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import CheckIcon from '@mui/icons-material/Check';
import { Box, CircularProgress } from '@mui/material';

import asyncService from '../../../datahub/asyncService';
import { dispatchErrors } from '../../../datahub/errorUtils';
import { PRIMARY_COLOR, PRIMARY_PURPLE } from '../../theme/colorConstants';
import { showSnackbar } from '../../views/app';
import WaeButton, { BUTTON_VARIANT } from '../Button';

const ActionButton = ({ onClick, disabled, loading, textKey }) => (
  <WaeButton
    variant={BUTTON_VARIANT.DEFAULT}
    actionColor={PRIMARY_COLOR[70]}
    sx={{ height: '28px', width: { xs: '100%', lg: 'auto' } }}
    onClick={onClick}
    disabled={disabled}
    loading={loading}
  >
    {loading ? <CircularProgress size={16} color="inherit" /> : LanguageConverterV2(textKey)}
  </WaeButton>
);

ActionButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  textKey: PropTypes.string.isRequired,
};

const WorkerActionButtons = ({ candidate, user, jobOrderId, onSuccess, isOverrideMode }) => {
  const dispatch = useDispatch();
  const [inviteLoading, setInviteLoading] = useState(false);
  const [addToShiftLoading, setAddToShiftLoading] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [addToShiftSuccess, setAddToShiftSuccess] = useState(false);

  const { token, refreshToken } = user || {};

  const isAdmin = user?.role === UserRole.ADMIN;
  const isRecruiter = user?.role === UserRole.RECRUITER;
  const isEmployer = user?.role === UserRole.EMPLOYER;
  const canInvite = isAdmin || isRecruiter || isEmployer;
  const canAddToShift = isAdmin;

  // Shared validation function to eliminate duplication
  const validateRequiredParams = () => {
    if (!jobOrderId || !candidate?._id) {
      dispatch(
        showSnackbar({
          message: 'error.missing.required.parameters',
        })
      );
      return false;
    }
    return true;
  };

  const handleInvite = async () => {
    if (!validateRequiredParams()) return;

    setInviteLoading(true);
    await asyncService({
      httpMethod: 'POST',
      route: 'v2/send-offer',
      data: {
        jobOrder: jobOrderId,
        candidates: [candidate._id],
      },
      token,
      refreshToken,
      onSuccess: () => {
        setInviteLoading(false);
        setInviteSuccess(true);
        if (onSuccess) {
          onSuccess();
        }
      },
      onError: (error) => {
        setInviteLoading(false);
        dispatchErrors({ dispatch, responsePayload: error.response });
      },
      dispatch,
    });
  };

  const handleAddToShift = async () => {
    if (!validateRequiredParams()) return;

    setAddToShiftLoading(true);
    await asyncService({
      httpMethod: 'POST',
      route: 'placements',
      data: {
        fields: {
          jobOrders: [jobOrderId],
          candidate: candidate._id,
        },
      },
      token,
      refreshToken,
      onSuccess: () => {
        setAddToShiftLoading(false);
        setAddToShiftSuccess(true);
        setInviteSuccess(true); // When added to shift, also mark as invited
        if (onSuccess) {
          onSuccess();
        }
      },
      onError: (error) => {
        setAddToShiftLoading(false);
        dispatchErrors({ dispatch, responsePayload: error.response });
      },
      dispatch,
    });
  };

  const successStyle = {
    color: PRIMARY_PURPLE[70],
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '14px',
    fontWeight: '600',
    padding: '8px',
  };

  // If both actions are successful, show "Added"
  if (addToShiftSuccess) {
    return (
      <Box sx={successStyle}>
        <CheckIcon fontSize="small" />
        {LanguageConverterV2('dashboard.recommendWorkerModal.added')}
      </Box>
    );
  }

  // If only invite is successful, show "Invited"
  if (inviteSuccess) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Box sx={successStyle}>
          <CheckIcon fontSize="small" />
          {LanguageConverterV2('dashboard.recommendWorkerModal.invited')}
        </Box>
        {canAddToShift && (
          <ActionButton
            onClick={handleAddToShift}
            disabled={addToShiftLoading}
            loading={addToShiftLoading}
            textKey={
              isOverrideMode
                ? 'dashboard.recommendWorkerModal.overrideAndAdd'
                : 'dashboard.recommendWorkerModal.addToShift'
            }
          />
        )}
      </Box>
    );
  }

  const inviteTextKey = isOverrideMode
    ? 'dashboard.recommendWorkerModal.overrideAndInvite'
    : 'dashboard.recommendWorkerModal.invite';
  const addToShiftTextKey = isOverrideMode
    ? 'dashboard.recommendWorkerModal.overrideAndAdd'
    : 'dashboard.recommendWorkerModal.addToShift';

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '8px',
        flexDirection: 'column',
        width: { xs: '100%', lg: 'auto' },
        alignItems: { xs: 'stretch', lg: 'center' },
      }}
    >
      {canInvite && (
        <ActionButton
          onClick={handleInvite}
          disabled={inviteLoading}
          loading={inviteLoading}
          textKey={inviteTextKey}
        />
      )}

      {canAddToShift && (
        <ActionButton
          onClick={handleAddToShift}
          disabled={addToShiftLoading}
          loading={addToShiftLoading}
          textKey={addToShiftTextKey}
        />
      )}
    </Box>
  );
};

WorkerActionButtons.propTypes = {
  candidate: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
  }),
  user: PropTypes.shape({
    role: PropTypes.string,
    token: PropTypes.string,
    refreshToken: PropTypes.string,
  }),
  jobOrderId: PropTypes.string,
  onSuccess: PropTypes.func,
  isOverrideMode: PropTypes.bool,
};

export default WorkerActionButtons;
